import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Check, LayoutDashboard, User, LogIn, Menu, X } from 'lucide-react';
import { glassNavbar } from "@/assets/svgs/gradientIcons";
import { TwinneeWordmark } from "@/components/TwinneeWordmark";

import { TwinneeLogo } from '@/components/TwinneeLogo';
const languages = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी (Hindi)" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "ja", label: "日本語" },
];



export const LandingNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedLang, setSelectedLang] = useState("English");
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeNav, setActiveNav] = useState(
    location.pathname === "/flows" ? "flows" : (location.hash ? location.hash.replace("#", "") : "")
  );
  const langRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("storycraft_token");
    setIsLoggedIn(Boolean(token));
  }, []);

  useEffect(() => {
    if (location.pathname === "/flows") {
      setActiveNav("flows");
    } else if (location.hash) {
      setActiveNav(location.hash.replace("#", ""));
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* Add subtle background on scroll for readability */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (id) => (e) => {
    e.preventDefault();
    setActiveNav(id);
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleLogoClick = () => {
    setActiveNav("");
    setMobileOpen(false);
    navigate("/");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getNavLinkClass = (id) => {
    const isActive = activeNav === id;
    if (isActive) {
      return "bg-gradient-to-r from-[#D0BCFF] to-[#62FAE3] bg-clip-text text-transparent font-bold tracking-tight transition-all";
    }
    return "text-gray-300/90 hover:text-white transition-colors cursor-pointer";
  };

  const navItems = [
    { id: "flows", label: "Flows", isLink: true },
    { id: "click-prompting", label: "Click Prompting" },
    { id: "who-it-is-for", label: "Who it is for" },
    { id: "comparison", label: "Why Twinnee" },
    { id: "pricing", label: "Pricing" },
    { id: "faq", label: "FAQ" },
  ];



  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 font-sora"
      style={{ transition: "background 0.3s ease" }}
    >
      {/* Exact Figma Header Background: #000000 6% opacity fill + linear gradient from top (50% opacity) to bottom (0% opacity) */}
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          background: `
            linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%),
            rgba(0, 0, 0, 0.06)
          `,
          backdropFilter: "blur(3px)",
        }}
      />

      {/* Subtle bottom border line – thin gradient */}
      <div
        className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent 0%, rgba(98,250,227,0.15) 30%, rgba(208,188,255,0.15) 70%, transparent 100%)",
        }}
      />

      {/* Header Content Container */}
      <div className="relative z-10 flex items-center justify-between w-full h-[72px] sm:h-[84px] max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Brand Logo */}
        <button
          onClick={handleLogoClick}
          aria-label="Twinnee home"
          className="flex items-center shrink-0 cursor-pointer bg-transparent border-none p-0 hover:opacity-90 transition-opacity"
        >
          <TwinneeWordmark className="h-8 sm:h-9" />
        </button>

        {/* Navigation Links — centered with dot separators */}
        <nav className="hidden md:flex items-center gap-7 lg:gap-9 text-[13px] font-medium font-body absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {navItems.map((item, i) => (
            <React.Fragment key={item.id}>
              {item.isLink ? (
                <Link
                  to="/flows"
                  onClick={() => setActiveNav("flows")}
                  className={getNavLinkClass("flows")}
                >
                  {item.label}
                </Link>
              ) : (
                <a
                  href={`#${item.id}`}
                  onClick={handleNavClick(item.id)}
                  className={getNavLinkClass(item.id)}
                >
                  {item.label}
                </a>
              )}

            </React.Fragment>
          ))}
        </nav>

        {/* Right Header Actions */}
        <div className="hidden md:flex items-center gap-4 sm:gap-5 text-sm shrink-0">
          {/* Language Dropdown */}
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIsLangOpen((prev) => !prev); }}
              className="bg-transparent border-none text-white/90 cursor-pointer flex items-center gap-1.5 text-xs font-medium hover:text-white transition-colors py-1.5 px-2.5 rounded-lg hover:bg-white/10 font-body"
            >
              <span>{selectedLang}</span>
              <ChevronDown className={`w-3.5 h-3.5 opacity-70 transition-transform duration-200 ${isLangOpen ? "rotate-180 text-[#62FAE3]" : ""}`} />
            </button>

            {isLangOpen && (
              <div className="absolute right-0 top-full mt-2 w-44 rounded-xl border border-white/15 bg-[#0a0d14]/95 backdrop-blur-xl shadow-2xl py-1.5 z-50 font-body">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { setSelectedLang(lang.label.split(" ")[0]); setIsLangOpen(false); }}
                    className={`w-full text-left px-3.5 py-2 text-xs flex items-center justify-between transition-colors hover:bg-white/10 cursor-pointer ${selectedLang === lang.label.split(" ")[0] ? "text-[#62FAE3] font-semibold bg-[#62FAE3]/10" : "text-gray-300"
                      }`}
                  >
                    <span>{lang.label}</span>
                    {selectedLang === lang.label.split(" ")[0] && <Check className="w-3.5 h-3.5 text-[#62FAE3]" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Conditional Auth / Dashboard Actions */}
          {isLoggedIn ? (
            /* Logged In: Dashboard CTA Button */
            <Link
              to="/dashboard"
              className="px-5 sm:px-6 py-2.5 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] text-[#06080d] font-bold text-xs font-headline tracking-wider hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-[#2DD4BF]/20 flex items-center gap-2 cursor-pointer"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>Dashboard</span>
            </Link>
          ) : (
            /* Not Logged In: Sign In & Sign Up Options */
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="text-xs font-semibold text-gray-300 hover:text-white transition-colors px-3 py-2 font-body cursor-pointer"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                className="px-5 sm:px-6 py-2.5 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] text-[#06080d] font-bold text-xs font-headline tracking-wider hover:brightness-110 active:scale-95 transition-all shadow-lg shadow-[#2DD4BF]/20 flex items-center gap-1.5 cursor-pointer"
              >
                <TwinneeLogo className="w-3.5 h-3.5" />
                <span>Sign Up</span>
              </Link>
            </div>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden relative z-10 p-2 text-white/90 hover:text-white transition-colors"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="md:hidden relative z-10 border-t border-white/[0.06] px-6 py-4 space-y-1 font-body"
          style={{
            background: "rgba(5,7,10,0.95)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          {navItems.map((item) =>
            item.isLink ? (
              <Link
                key={item.id}
                to="/flows"
                onClick={() => { setActiveNav("flows"); setMobileOpen(false); }}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  activeNav === item.id
                    ? "bg-gradient-to-r from-[#D0BCFF]/15 to-[#62FAE3]/15 text-white font-bold"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={handleNavClick(item.id)}
                className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  activeNav === item.id
                    ? "bg-gradient-to-r from-[#D0BCFF]/15 to-[#62FAE3]/15 text-white font-bold"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                {item.label}
              </a>
            )
          )}

          {/* Mobile auth actions */}
          <div className="pt-3 mt-2 border-t border-white/[0.06] flex items-center gap-3">
            {isLoggedIn ? (
              <Link
                to="/dashboard"
                onClick={() => setMobileOpen(false)}
                className="w-full text-center px-5 py-2.5 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] text-[#06080d] font-bold text-xs tracking-wider"
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="text-xs font-semibold text-gray-300 hover:text-white transition-colors px-3 py-2"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setMobileOpen(false)}
                  className="flex-1 text-center px-5 py-2.5 rounded-full bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#2DD4BF] text-[#06080d] font-bold text-xs tracking-wider flex items-center justify-center gap-1.5"
                >
                  <TwinneeLogo className="w-3.5 h-3.5" />
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default LandingNavbar;
